import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { RemovingThrobber } from "../../removing_throbber";
import { GuildLogItem } from "./guild_log_item";

export function GuildLogList(props: {
	logItemCreator: (
		log: RouterInferenceHelpers["guild_logs"]["get"]["output"]["logs"][0]
	) => JSX.Element;
	logs_query: {
		hasNextPage: boolean;
		isFetching: boolean;
		isFetchingNextPage: boolean;
		isLoading: boolean;
		isRefetching: boolean;
		fetchNextPage: () => void;
		data: {
			pages: {
				logs: RouterInferenceHelpers["guild_logs"]["get"]["output"]["logs"];
			}[];
		};
	};
}) {
	const {
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
		isFetching,
		isLoading,
		isRefetching,
	} = props.logs_query;
	const logs = props.logs_query.data?.pages.flatMap((val) => val.logs) ?? [];

	const parentRef = useRef();

	const virtualizer = useVirtualizer({
		count: hasNextPage ? logs.length + 1 : logs.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 90,
		overscan: 3,
	});

	useEffect(() => {
		const [lastItem] = [...virtualizer.getVirtualItems()].reverse();

		if (!lastItem) {
			return;
		}

		if (
			lastItem.index >= logs.length - 1 &&
			hasNextPage &&
			!isFetchingNextPage
		) {
			fetchNextPage();
		}
	}, [
		hasNextPage,
		fetchNextPage,
		logs.length,
		isFetchingNextPage,
		virtualizer.getVirtualItems(),
	]);

	// const is_initial_fetch = !(!isRefetching || isFetchingNextPage);
	const is_initial_fetch = isLoading || (isRefetching && !isFetchingNextPage);

	if (is_initial_fetch) {
		return (
			<div className="h-[500px]">
				<PlaceholderRow>
					<div className="flex h-full items-center gap-2">
						Loading <RemovingThrobber />
					</div>
				</PlaceholderRow>
			</div>
		);
	}

	return (
		<div ref={parentRef} className="h-[500px] w-full overflow-auto px-2">
			<div
				style={{
					height: `${virtualizer.getTotalSize()}px`,
					width: "100%",
					position: "relative",
				}}
			>
				{virtualizer.getVirtualItems().map((virtualRow) => {
					const isLoaderRow = virtualRow.index > logs.length - 1;

					const post = logs[virtualRow.index];

					return (
						<div
							key={virtualRow.index}
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								transform: `translateY(${virtualRow.start}px)`,
							}}
						>
							{isLoaderRow ? (
								<PlaceholderRow>
									<div className="flex h-full items-center gap-2">
										Loading <RemovingThrobber />
									</div>
								</PlaceholderRow>
							) : (
								<div
									data-index={virtualRow.index}
									ref={virtualizer.measureElement}
								>
									<div className="pt-1">
										{props.logItemCreator(post)}
									</div>
									{!hasNextPage &&
										virtualRow.index ===
											logs.length - 1 && (
											<PlaceholderRow>
												End of Logs
											</PlaceholderRow>
										)}
								</div>
							)}
						</div>
					);
				})}
				{!isFetching && logs.length === 0 && (
					<PlaceholderRow>End of Logs</PlaceholderRow>
				)}
			</div>
		</div>
	);
}

function PlaceholderRow(props: { children: React.ReactNode }) {
	return (
		<div className="pt-1">
			<div className="flex flex-col items-center justify-center gap-0 bg-base-light py-6 text-sesh-dark">
				{props.children}
			</div>
		</div>
	);
}
